import ApiTags from '@constants/api-tags';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useAppDispatch } from '@core/store';
import { useLazyGetOrganizationListQuery } from '@features/authentication/queries/AuthQuery';
import formsApi from '@features/forms/queries/FormsQuery';
import useNotify from '@hooks/notify';
import { updateBranchFilter } from '@layouts/main-header/branchSlice';
import {
  setCurrentOrg,
  setNoBranchAccess
} from '@layouts/main-header/userSlice';
import {
  clearActiveBranchFromStorage,
  getLocalStorageItem,
  setMultipleLocalStorageItems
} from '@utils/storage';

/**
 * Once the selected branch is hidden or archived the user can no longer work in it,
 * so move them to the default branch the backend picks for them (same as at login).
 * When no visible branch is left, the no-branch modal takes over instead.
 */
const useSwitchToDefaultBranch = () => {
  const dispatch = useAppDispatch();
  const { createErrorAlert } = useNotify();
  const [getOrganizationList] = useLazyGetOrganizationListQuery();

  const switchToDefaultBranch = async (organizationId: string) => {
    const res = await getOrganizationList(null);
    if ('error' in res || !res.data) {
      createErrorAlert('Unable to switch branch. Please select a branch.');
      return;
    }
    const org = res.data.find((item) => item.id === organizationId);
    if (!org) return;
    if (!org.default_branch_id) {
      // Drop the lost branch so neither the switcher nor a refresh shows it.
      clearActiveBranchFromStorage();
      dispatch(updateBranchFilter({ branchId: '', branchName: '' }));
      dispatch(setNoBranchAccess(true));
      return;
    }

    setMultipleLocalStorageItems([
      { key: LOCAL_STORAGE_KEYS.BRANCH_ID, value: org.default_branch_id },
      { key: LOCAL_STORAGE_KEYS.BRANCH_NAME, value: org.default_branch_name }
    ]);
    dispatch(setCurrentOrg(org));
    dispatch(
      updateBranchFilter({
        branchId: org.default_branch_id,
        branchName: org.default_branch_name
      })
    );
    dispatch(
      formsApi.util.invalidateTags([ApiTags.FORM_LIST, ApiTags.FORM_DETAIL])
    );
  };

  const isCurrentBranch = (branchId: string) =>
    branchId === getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID);

  return { switchToDefaultBranch, isCurrentBranch };
};

export default useSwitchToDefaultBranch;
